import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { frFR } from '@mui/x-date-pickers/locales';
import { Box, Typography } from '@mui/material';

interface AppDatePickerProps {
  readonly label?: string;
  readonly required?: boolean;
  readonly error?: boolean;
  readonly helperText?: string;
  readonly value?: Date | null;
  readonly onChange?: (value: Date | null) => void;
  readonly onBlur?: () => void;
  readonly disabled?: boolean;
  readonly minDate?: Date;
  readonly maxDate?: Date;
}

/**
 * Reusable date picker component with consistent styling and behavior.
 * Uses date-fns adapter for date manipulation and formatting.
 *
 * Features:
 * - Consistent 10px border radius (matches AppButton)
 * - 50px height for comfortable touch targets
 * - French locale by default (DD/MM/YYYY format)
 * - Optional label with required indicator
 * - Error state and helper text support
 * - Responsive design
 *
 * @example
 * ```tsx
 * <AppDatePicker
 *   label="Start Date"
 *   required
 *   value={startDate}
 *   onChange={(date) => setStartDate(date)}
 *   error={!!errors.startDate}
 *   helperText={errors.startDate?.message}
 * />
 * ```
 */
export function AppDatePicker({
  label,
  required = false,
  error = false,
  helperText,
  value,
  onChange,
  onBlur,
  disabled = false,
  minDate,
  maxDate,
}: Readonly<AppDatePickerProps>) {
  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={fr}
      localeText={frFR.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <Box>
        {label && (
          <Typography
            component="label"
            sx={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              color: error ? 'error.main' : 'text.primary',
              mb: 1,
            }}
          >
            {label}
            {required && (
              <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                *
              </Typography>
            )}
          </Typography>
        )}
        <DatePicker
          value={value}
          onChange={onChange}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          format="dd/MM/yyyy"
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
              error,
              helperText,
              placeholder: 'JJ/MM/AAAA',
              onBlur,
              sx: {
                '& .MuiInputBase-root': {
                  minHeight: '44px',
                  borderRadius: '10px',
                  fontSize: '16px',
                },
                '& .MuiInputBase-input': {
                  padding: '8px 12px',
                  fontSize: { xs: '16px', sm: '14px' },
                  height: 'auto',
                },
              },
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
}
